import React, { useEffect, useRef, useState, useCallback } from "react";

import { formatDateForPanelDisplay } from "@/Utils/helper";

import { useLibraryDocumentViewer } from "../../context/LibraryDocumentViewerContext";

const renderDataRow = (setRowRef, tableRowStyle, doc, index, handleDocPreview) => (
  <tr
    ref={(el) => setRowRef(el, index)}
    key={`doc-${index}`}
    onClick={() => handleDocPreview(doc)}
    style={tableRowStyle}
  >
    <td
      className="text-center"
      style={{
        minWidth: "25px",
        width: "25px",
        maxWidth: "25px",
        color: "var(--primary-25)",
        fontWeight: "600",
      }}
    >
      {index + 1}
    </td>

    <td className="p-l-5">
      <div className="d-flex p-l-5 height-21 align-items-center">
        <span
          className="height-21 d-flex align-items-center"
          style={{ fontWeight: "600", gap: "5px" }}
        >
          <i className="ic ic-file-colored ic-15" />
          <span title={doc?.document_name} className="text-truncate">
            {doc?.document_name}
          </span>
        </span>
      </div>
    </td>
    <td />
    <td className="p-l-5">
      <div className="d-flex align-items-center height-21" style={{ fontWeight: "600" }}>
        <span title={doc?.source_client_name} className="text-truncate">
          {doc?.source_client_name ?? ""}
        </span>
      </div>
    </td>
    <td />
    <td>
      <div
        className="d-flex align-items-center text-center justify-content-center"
        style={{ fontWeight: "600", gap: "5px" }}
      >
        {doc?.case_type?.casetype_icon && (
          <span className="d-flex ic-15 align-items-center justify-content-center">
            <img
              src={doc?.case_type?.casetype_icon}
              className="w-100 d-flex align-items-center justify-content-center"
            />
          </span>
        )}
        {doc?.case_type?.name ?? ""}
      </div>
    </td>

    <td />
    <td className="text-end">
      <div className="d-flex flex-column pe-2" style={{ fontWeight: "600" }}>
        {formatDateForPanelDisplay(doc?.created_at)}
      </div>
    </td>
    <td />
  </tr>
);

const renderEmptyRows = (emptyRowStyle, tableRowStyle, fakeRows) => {
  const emptyRowsCount = Math.max(0, fakeRows);
  return Array.from({ length: emptyRowsCount }, (_, index) => (
    <tr key={`empty-${index}`} style={tableRowStyle}>
      <td colSpan={9} className="empty-row height-25">
        <div style={emptyRowStyle}>{/* Empty row */}</div>
      </td>
    </tr>
  ));
};

const renderPartialRow = (partialHeight) => {
  if (partialHeight <= 0) return null;
  return (
    <tr
      key="partial-row"
      style={{ height: `${partialHeight}px`, lineHeight: `${partialHeight}px` }}
    >
      <td colSpan={9} className="empty-row" style={{ height: `${partialHeight}px` }}>
        <div style={{ height: `${partialHeight}px`, width: "100%" }} />
      </td>
    </tr>
  );
};

function LibrarySummaryTab({ doc, containerHeight, refetchData }) {
  const [lineHeight, setLineHeight] = useState(25);
  const tableRowStyle = { height: `${lineHeight}px`, lineHeight: `${lineHeight}px` };
  const emptyRowStyle = { height: `${lineHeight}px`, width: "100%" };

  const rowRefs = useRef({});
  const setRowRef = useCallback((el, index) => {
    if (el) rowRefs.current[index] = el;
    else delete rowRefs.current[index];
  }, []);

  const [fakeRows, setFakeRows] = useState(0);
  const [partialHeight, setPartialHeight] = useState(0);
  const [canScroll, setCanScroll] = useState(false);

  useEffect(() => {
    const heights = Object.values(rowRefs.current).map((el) => el.offsetHeight);
    if (heights.length === 0) return;

    const maxHeight = Math.max(...heights);
    if (maxHeight && maxHeight !== lineHeight) {
      setLineHeight(maxHeight);
    }
  }, [doc?.data]);

  useEffect(() => {
    if (!containerHeight?.current) return;

    const height = containerHeight.current.offsetHeight;
    const availableHeight = height - 90 - 25;
    const rowHeight = lineHeight;
    const dataLength = doc?.data?.length || 0;

    const totalRows = Math.floor(availableHeight / rowHeight);
    const remainder = availableHeight - totalRows * rowHeight;

    if (dataLength > totalRows) {
      setFakeRows(0);
      setPartialHeight(0);
      setCanScroll(true);
    } else {
      setFakeRows(totalRows - dataLength);
      setPartialHeight(remainder);
      setCanScroll(false);
    }
  }, [containerHeight, doc?.data?.length, lineHeight]);
  const { openDocument } = useLibraryDocumentViewer();

  const handleDocPreview = (d) => {
    openDocument(d, { onUpdated: refetchData });
  };

  return (
    <div className="custom-tab lit-custom-tab w-100 m-b-5">
      <div
        className="table-responsive"
        style={{ overflow: canScroll ? "auto" : "hidden", scrollbarWidth: "none" }}
      >
        <table className="table table-borderless table-striped table-treatment has-height-25">
          <thead>
            <tr style={{ position: "sticky", top: "0px" }} id="tb-header">
              <th
                className="td-autosize text-center"
                style={{ minWidth: "25px", width: "25px", maxWidth: "25px" }}
              />
              <th className="td-autosize text-center color-grey-2 p-l-5">Document Name</th>
              <th />
              <th className="td-autosize text-center color-grey-2 p-l-5">Client</th>
              <th />
              <th className="td-autosize text-center color-grey-2">Case Type</th>
              <th />
              <th className="td-autosize text-center color-grey-2">Added</th>
              <th style={{ minWidth: "5px", maxWidth: "5px", width: "5px" }} />
            </tr>
          </thead>
          <tbody>
            {doc?.data?.map((doc1, index) =>
              renderDataRow(setRowRef, tableRowStyle, doc1, index, handleDocPreview)
            )}
            {!canScroll && renderEmptyRows(emptyRowStyle, tableRowStyle, fakeRows)}
            {!canScroll && renderPartialRow(partialHeight)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default React.memo(LibrarySummaryTab);
